'use client';

import React, { useEffect, useState, useRef } from 'react';
import { Button } from "../ui/button";
import { Loader2, Download, Upload } from "lucide-react";
import { pdf } from '@react-pdf/renderer';
import { NewProjectDefinitionPDF } from './NewProjectDefinitionPDF';
import { backendApiUrl } from '@/lib/config';
import axios from 'axios';
import { showErrorToast, showSuccessToast } from '../toast/toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,

} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetProjectMediaQuery } from '@/redux/slices/media/mediaApiSlice';
import oktaService from '@/services/OktaService';
type ImageCategory = "gpsRoute" | "preship" | "postship" | "stacking";

// Debug settings - can be turned on in development if needed
const DEBUG_IMAGE_LOADING = false; // Set to true for detailed logs
const FORCE_RELOAD_IMAGES = false; // Set to true to bypass cache and reload all images

interface MediaItem {
  id: number;
  url: string;
  contentType: 'image' | 'video';
  type: ImageCategory;
  palletId: number;
  localUrl?: string; // Added for cached URLs
  isLoading?: boolean; // Loading state for images
  hasError?: boolean; // Error state when image fails to load
}

interface DownloadButtonProps {
  projectId: number;
  disabled?: boolean;
  showPushTo3DX?: boolean;
  pushTo3DXDisabled?: boolean;
  isPushingPdf?: boolean;
  onPushTo3Dx?: () => void | Promise<void>;
  setPushTo3DXPayload?: React.Dispatch<React.SetStateAction<{ sections: string[]; imageIds: number[] }>>;
}

export interface DownloadButtonHandle {
  open: () => void;
}
const disabledOptions = [
  {
    id: 'projectDetails',
    label: 'Project Details'
  },
  {
    id: 'synopsis',
    label: 'Synopsis'
  },
  {
    id: 'summary',
    label: 'Summary'
  }
]
const reportOptions = [
  { id: 'palletConfig', label: 'Pallet Configuration' },
  { id: 'lpn', label: 'LPN' },
  { id: 'preShip', label: 'Pre Ship' },
  { id: 'truckLayout', label: 'Truck Layout' },
  { id: 'postShip', label: 'Post Ship' },
  { id: 'stackTestLayout', label: 'Stack Test Layout' },
  { id: 'postStack', label: 'Post Stack' },
];

const DownloadButton = React.forwardRef<DownloadButtonHandle, DownloadButtonProps>(({
  projectId,
  disabled = false,
  showPushTo3DX = false,
  pushTo3DXDisabled = true,
  isPushingPdf = false,
  onPushTo3Dx,
  setPushTo3DXPayload,
}, ref) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIntent, setModalIntent] = useState<'report' | 'push3dx'>('report');

  React.useImperativeHandle(ref, () => ({
    open: () => {
      setModalIntent('push3dx');
      setIsModalOpen(true);
    },
  }));
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<number[]>([]);
  const [groupedMedia, setGroupedMedia] = useState<Record<number, Record<ImageCategory, MediaItem[]>>>({});
  const [currentPalletId, setCurrentPalletId] = useState<string>('');
  const [currentImageTab, setCurrentImageTab] = useState<string>('preship');
  const { palletTypes } = useSelector((state: RootState) => state.pallets);
  const { data: projectMedia, isLoading: isMediaLoading } = useGetProjectMediaQuery(projectId);

  // We use a cache for images to avoid CORS issues
  const imageBlobCache = useRef<Map<string, string>>(new Map());
  const [processedMedia, setProcessedMedia] = useState<Record<number, MediaItem>>({});

  useEffect(() => {
    if (palletTypes.length > 0) {
      setCurrentPalletId(palletTypes[0]?.id?.toString() || '');
    }
  }, [palletTypes]);

  useEffect(() => {
    if (projectMedia) {
      const newGroupedMedia = projectMedia.reduce((acc: Record<number, Record<ImageCategory, MediaItem[]>>, item: MediaItem) => {
        if (item.contentType !== 'image') return acc;

        if (!acc[item.palletId]) {
          acc[item.palletId] = {} as Record<ImageCategory, MediaItem[]>;
        }
        if (!acc[item.palletId][item.type]) {
          acc[item.palletId][item.type] = [];
        }

        // Add loading state properties
        const processedItem = {
          ...item,
          isLoading: true,
          hasError: false
        };

        acc[item.palletId][item.type].push(processedItem);

        // Also add to our processedMedia for easy lookup
        setProcessedMedia(prev => ({
          ...prev,
          [item.id]: processedItem
        }));

        return acc;
      }, {} as Record<number, Record<ImageCategory, MediaItem[]>>);

      setGroupedMedia(newGroupedMedia);

      // Preload all images
      Object.values(newGroupedMedia).forEach(categoryGroups => {
        Object.values(categoryGroups).forEach(mediaItems => {
          mediaItems.forEach(item => {
            if (item.contentType === 'image') {
              preloadImage(item);
            }
          });
        });
      });
    }

    // Cleanup function to revoke any blob URLs we created
    return () => {
      Object.values(processedMedia).forEach(item => {
        if (item.localUrl && item.localUrl.startsWith('blob:')) {
          URL.revokeObjectURL(item.localUrl);
        }
      });
    };
  }, [projectMedia]);

  // Helper function to get full URL from relative URL
  const getFullMediaUrl = (url: string): string => {
    // If it's a blob URL or already absolute URL (starts with http/https), use as is
    if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // For API media image endpoints, we need special handling
    if (url.startsWith('/api/media/image/')) {
      // This is already a properly formatted proxy URL, we just need to make sure it points to the backend
      const blobName = url.split('/api/media/image/')[1];
      const fullUrl = `${backendApiUrl}/media/image/${blobName}`;
      if (DEBUG_IMAGE_LOADING) console.log(`🖼️ Image proxy URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // If it's a relative URL (like /api/something), prepend the API URL properly
    if (url.startsWith('/api/')) {
      const fullUrl = `${backendApiUrl}${url.substring(4)}`;
      if (DEBUG_IMAGE_LOADING) console.log(`🖼️ API URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // For other relative URLs
    if (url.startsWith('/')) {
      const fullUrl = `${backendApiUrl}${url}`;
      if (DEBUG_IMAGE_LOADING) console.log(`🖼️ Relative URL: ${url} -> ${fullUrl}`);
      return fullUrl;
    }

    // Default case, just return the URL
    if (DEBUG_IMAGE_LOADING) console.log(`🖼️ Using original URL: ${url}`);
    return url;
  };

  // Function to preload an image and update the state once loaded
  const preloadImage = async (item: MediaItem) => {
    // Max number of retries
    const MAX_RETRIES = 2;
    let retryCount = 0;
    let lastError: Error | null = null;

    // Try loading the image with retries
    while (retryCount <= MAX_RETRIES) {
      try {
        // Check if we already have this cached (unless we're forcing a reload)
        if (!FORCE_RELOAD_IMAGES && imageBlobCache.current.has(item.url)) {
          if (DEBUG_IMAGE_LOADING) {
            console.log(`Using cached image for: ${item.url}`);
          }
          updateMediaItemLocalUrl(item.id, imageBlobCache.current.get(item.url) as string);
          return;
        }

        // Get the URL that points to our backend
        const fullUrl = getFullMediaUrl(item.url);

        if (DEBUG_IMAGE_LOADING || retryCount > 0) {
          console.log(`Preloading image from: ${fullUrl}${retryCount > 0 ? ` (retry ${retryCount}/${MAX_RETRIES})` : ''}`);
        }

        // Get Okta token for authorization
        let headers: Record<string, string> = {};
        try {
          const oktaUser = await oktaService.getUser();
          if (oktaUser.access_token) {
            headers = {
              'Authorization': `Bearer ${oktaUser.access_token}`
            };
            if (DEBUG_IMAGE_LOADING) {
              console.log('Added authorization header for image fetch');
            }
          }
        } catch (tokenError) {
          console.warn('Could not get Okta token for image fetch:', tokenError);
          // Continue without auth header - might still work for public resources
        }

        const response = await fetch(fullUrl, {
          headers,
          credentials: 'include', // Include cookies if needed
          cache: FORCE_RELOAD_IMAGES ? 'no-store' : 'default' // Don't use cached responses when forcing reload
        });

        if (!response.ok) {
          // For 401/403 errors, we might need a new token, so retry
          if (response.status === 401 || response.status === 403) {
            throw new Error(`Authentication error: ${response.status} ${response.statusText}`);
          }

          throw new Error(`Failed to preload image: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Cache this URL for future use
        imageBlobCache.current.set(item.url, blobUrl);

        // Update the item in our state
        updateMediaItemLocalUrl(item.id, blobUrl);

        if (DEBUG_IMAGE_LOADING) {
          console.log(`Successfully loaded image: ${item.url}`);
        }

        // Success, exit the retry loop
        return;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        console.error(`Error preloading image (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, error);

        if (retryCount < MAX_RETRIES) {
          // Exponential backoff delay between retries (300ms, then 900ms)
          const delay = 300 * Math.pow(3, retryCount);
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          retryCount++;
        } else {
          // All retries failed
          break;
        }
      }
    }

    // All attempts failed
    console.error(`All ${MAX_RETRIES + 1} attempts to load image failed`, lastError);
    updateMediaItemLoadingState(item.id, false, true);
  };

  // Helper to update a specific media item's local URL in all states
  const updateMediaItemLocalUrl = (id: number, localUrl: string) => {
    // Update in processedMedia
    setProcessedMedia(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        localUrl,
        isLoading: false,
        hasError: false
      }
    }));

    // Update in groupedMedia (this is more complex since it's nested)
    setGroupedMedia(prev => {
      const updatedMedia = { ...prev };

      // Find the item in the nested structure and update it
      Object.keys(updatedMedia).forEach(palletIdKey => {
        const palletId = Number(palletIdKey);
        const palletGroup = updatedMedia[palletId];

        // Add null check for palletGroup
        if (!palletGroup) return;

        Object.keys(palletGroup).forEach(typeKey => {
          const type = typeKey as ImageCategory;
          const mediaItems = palletGroup[type];

          // Add null check for mediaItems
          if (!mediaItems || !Array.isArray(mediaItems)) return;

          const itemIndex = mediaItems.findIndex(item => item.id === id);
          if (itemIndex >= 0) {
            mediaItems[itemIndex] = {
              ...mediaItems[itemIndex],
              localUrl,
              isLoading: false,
              hasError: false
            };
          }
        });
      });

      return updatedMedia;
    });
  };

  // Helper to update a specific media item's loading/error state
  const updateMediaItemLoadingState = (id: number, isLoading: boolean, hasError: boolean = false) => {
    // Update in processedMedia
    setProcessedMedia(prev => ({
      ...prev,
      [id]: { ...prev[id], isLoading, hasError }
    }));

    // Update in groupedMedia
    setGroupedMedia(prev => {
      const updatedMedia = { ...prev };

      Object.keys(updatedMedia).forEach(palletIdKey => {
        const palletId = Number(palletIdKey);
        const palletGroup = updatedMedia[palletId];

        // Add null check for palletGroup
        if (!palletGroup) return;

        Object.keys(palletGroup).forEach(typeKey => {
          const type = typeKey as ImageCategory;
          const mediaItems = palletGroup[type];

          // Add null check for mediaItems
          if (!mediaItems || !Array.isArray(mediaItems)) return;

          const itemIndex = mediaItems.findIndex(item => item.id === id);
          if (itemIndex >= 0) {
            mediaItems[itemIndex] = { ...mediaItems[itemIndex], isLoading, hasError };
          }
        });
      });

      return updatedMedia;
    });
  };

  const handleSelectAll = () => {
    setSelectedOptions(reportOptions.map(option => option.id));
  };

  const handleReset = () => {
    setSelectedOptions([]);
    setSelectedImages([]);
  };

  const handleOptionToggle = (optionId: string) => {
    setSelectedOptions(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleImageToggle = (imageId: number) => {
    setSelectedImages(prev =>
      prev.includes(imageId)
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  useEffect(()=>{
    setPushTo3DXPayload({
    sections: selectedOptions,
    imageIds: selectedImages
  })
  },[selectedOptions,selectedImages])

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const oktaUser = await oktaService.getUser();
      if (oktaUser.access_token) {
        const response = await axios.get(
          `${backendApiUrl}/projects/summary/download-report/${projectId}`,
          {
            params: {
              sections: selectedOptions,
              images: selectedImages
            },
            withCredentials: true,
            headers: {
              "Authorization": `Bearer ${oktaUser.access_token}`
            },
          }
        );
        console.log(response.data, 'response.data');
        if (response.data.success) {
          const pdfData = response.data.projectDefinition;

          // The report endpoint and the live pallet query use slightly different
          // shapes. Fill report-only gaps from the pallet data already loaded for
          // this project without overwriting values returned by the report API.
          if (Array.isArray(pdfData.pallets)) {
            pdfData.pallets = pdfData.pallets.map((reportPallet: any) => {
              const livePallet = palletTypes.find((pallet: any) => (
                String(pallet.id) === String(reportPallet.id) || pallet.name === reportPallet.name
              ));
              if (!livePallet) return reportPallet;
              const populated = (value: any) => {
                if (value === undefined || value === null || value === '') return false;
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === 'object') return Object.keys(value).length > 0;
                return true;
              };
              const preferReport = (reportValue: any, liveValue: any) => populated(reportValue) ? reportValue : liveValue;

              return {
                ...livePallet,
                ...reportPallet,
                shippingModes: preferReport(reportPallet.shippingModes, livePallet.shippingModes),
                numberOfPallets: preferReport(reportPallet.numberOfPallets, livePallet.numberOfPallets),
                primaryPackaging: preferReport(reportPallet.primaryPackaging, preferReport(reportPallet.primaryPackagingDetails, livePallet.primaryPackagingDetails)),
                secondaryPackaging: preferReport(reportPallet.secondaryPackaging, preferReport(reportPallet.secondaryPackagingDetails, livePallet.secondaryPackagingDetails)),
                tertiaryPackaging: preferReport(reportPallet.tertiaryPackaging, preferReport(reportPallet.tertiaryPackagingDetails, livePallet.tertiaryPackagingDetails)),
                palletDetails: preferReport(reportPallet.palletDetails, livePallet.palletDetails),
                unitLoadDetails: preferReport(reportPallet.unitLoadDetails, livePallet.unitLoadDetails),
                palletizingRecipe: preferReport(reportPallet.palletizingRecipe, preferReport(reportPallet.palletizingRecipes, livePallet.palletizingRecipes)),
              };
            });
          }

          if (pdfData.creator === undefined && pdfData.basicInfo && pdfData.basicInfo.createdBy) {
            pdfData.creator = {
              id: 0,
              name: pdfData.basicInfo.createdBy,
              role: 'Project Owner'
            };
          }

          // Fetch synopsis data
          try {
            console.log('fetching synopsis data');
            const synopsisResponse = await axios.get(
              `${backendApiUrl}/projects/summary/getSynopsisData/${projectId}`,
              {
                withCredentials: true,
                headers: {
                  "Authorization": `Bearer ${oktaUser.access_token}`
                },
              }
            );
            console.log(synopsisResponse, 'synopsisResponse.data');
            if (synopsisResponse.data && synopsisResponse.data) {
              pdfData.synopsisData = synopsisResponse.data;
            }

            // Also get pallets for test stack (needed for synopsis stack test table)
            const stackResponse = await axios.get(
              `${backendApiUrl}/projects/getPalletsForTestStack/${projectId}`,
              {
                withCredentials: true,
                headers: {
                  "Authorization": `Bearer ${oktaUser.access_token}`
                },
              }
            );
            console.log(stackResponse.data, 'stackResponse.data')
            if (stackResponse.data) {

              // Process pallets data to include recommended min pallets
              const stackPatterns = [
                { name: "2-HIGH COLUMN", minPallets: 6 },
                { name: "2-HIGH PYRAMID", minPallets: 6 },
                { name: "3-HIGH COLUMN", minPallets: 12 },
                { name: "3-HIGH PYRAMID", minPallets: 10 },
                { name: "4-HIGH COLUMN", minPallets: 16 },
                { name: "4-HIGH PYRAMID", minPallets: 14 },
              ];

              const processedPallets = stackResponse.data.map(pallet => ({
                ...pallet,
                recommendedMinimumPallets: stackPatterns.find(p => p.name === pallet.stackPattern)?.minPallets || 0
              }));

              pdfData.palletsForTestStack = processedPallets;
            }
          } catch (error) {
            console.error('Error fetching synopsis data:', error);
            // Continue without synopsis data
          }

          // Fetch summary data for all tabs
          try {
            pdfData.summaryData = {};

            // Pre-Ship Lean
            const preShipLeanResponse = await axios.get(
              `${backendApiUrl}/projects/getLeanConsolidatedData/${projectId}/preShip`,
              {
                withCredentials: true,
                headers: {
                  "Authorization": `Bearer ${oktaUser.access_token}`
                },
              }
            );
            if (preShipLeanResponse.data) {
              // Pass the complete data structure instead of just additionalStats
              pdfData.summaryData.preShipLean = preShipLeanResponse.data;
            }

            // Post-Ship Lean
            const postShipLeanResponse = await axios.get(
              `${backendApiUrl}/projects/getLeanConsolidatedData/${projectId}/postShip`,
              {
                withCredentials: true,
                headers: {
                  "Authorization": `Bearer ${oktaUser.access_token}`
                },
              }
            );
            if (postShipLeanResponse.data) {
              // Pass the complete data structure
              pdfData.summaryData.postShipLean = postShipLeanResponse.data;
            }

            // Unit Load Position
            const ulpResponse = await axios.get(
              `${backendApiUrl}/projects/getUnitLoadPositionConsolidatedData/${projectId}/postShip`,
              {
                withCredentials: true,
                headers: {
                  "Authorization": `Bearer ${oktaUser.access_token}`
                },
              }
            );
            if (ulpResponse.data && ulpResponse.data.additionalStats) {
              pdfData.summaryData.unitLoadPosition = ulpResponse.data;
            }

            // Pre-Ship Force To Load
            const preShipFTLResponse = await axios.get(
              `${backendApiUrl}/projects/getFTLConsolidatedData/${projectId}/preShip`,
              {
                withCredentials: true,
                headers: {
                  "Authorization": `Bearer ${oktaUser.access_token}`
                },
              }
            );
            if (preShipFTLResponse.data && preShipFTLResponse.data.additionalStats) {
              pdfData.summaryData.preShipFTL = preShipFTLResponse.data;
            }

            // Post-Ship Force To Load
            const postShipFTLResponse = await axios.get(
              `${backendApiUrl}/projects/getFTLConsolidatedData/${projectId}/postShip`,
              {
                withCredentials: true,
                headers: {
                  "Authorization": `Bearer ${oktaUser.access_token}`
                },
              }
            );
            if (postShipFTLResponse.data && postShipFTLResponse.data.additionalStats) {
              pdfData.summaryData.postShipFTL = postShipFTLResponse.data;
            }

            const reportRequest = (path: string) => axios.get(`${backendApiUrl}${path}`, {
              withCredentials: true,
              headers: { "Authorization": `Bearer ${oktaUser.access_token}` },
            });

            const stackLeanWeeks = [
              { label: 'Week 1', weekType: 'Stack Week-1' },
              { label: 'Week 4', weekType: 'Stack Week-4 (unstacking+30M)' },
            ];
            const stackHeightMeasurements = [
              { label: 'Height Compression', weekType: 'Post stack unit load height (with load still on)' },
              { label: 'Height Rebound', weekType: 'Post stack unit load with Rebound (30 min post load removal)' },
            ];

            const [leanResults, heightResults] = await Promise.all([
              Promise.allSettled(stackLeanWeeks.map(({ weekType }) => reportRequest(
                `/projects/getPostStackLeanConsolidatedData/${projectId}/postStack/${encodeURIComponent(weekType)}`,
              ))),
              Promise.allSettled(stackHeightMeasurements.map(({ weekType }) => reportRequest(
                `/projects/getPostStackUnitLoadHeightConsolidatedData/${projectId}/postStack/${encodeURIComponent(weekType)}`,
              ))),
            ]);

            pdfData.summaryData.stackTestLeanByWeek = leanResults.flatMap((result, index) => (
              result.status === 'fulfilled' && result.value.data
                ? [{ label: stackLeanWeeks[index].label, data: result.value.data }]
                : []
            ));
            pdfData.summaryData.stackTestHeightByMeasurement = heightResults.flatMap((result, index) => (
              result.status === 'fulfilled' && result.value.data
                ? [{ label: stackHeightMeasurements[index].label, data: result.value.data }]
                : []
            ));
          } catch (error) {
            console.error('Error fetching summary data:', error);
            // Continue without summary data
          }

          // Prepare selected images for PDF
          const selectedMediaItems = selectedImages.map(imageId => {
            const mediaItem = processedMedia[imageId];
            if (!mediaItem) return null;

            // Create a new object with only the necessary properties for the PDF
            return {
              id: mediaItem.id,
              url: mediaItem.localUrl || getFullMediaUrl(mediaItem.url), // Use the cached URL if available
              type: mediaItem.type,
              palletId: mediaItem.palletId
            };
          }).filter(Boolean); // Filter out any null items

          console.log('Selected media items for PDF:', selectedMediaItems);

          const pdfDoc = <NewProjectDefinitionPDF
            data={pdfData}
            selectedOptions={selectedOptions}
            selectedImages={selectedMediaItems}
            palletTypes={palletTypes}
          />;
          const blob = await pdf(pdfDoc).toBlob();

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `project-definition-${Date.now()}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          showSuccessToast("Report downloaded successfully");
          setIsModalOpen(false);
        } else {
          showErrorToast("Failed to fetch report data");
        }
      } else {
        showErrorToast("Failed to download report");
      }

    } catch (error) {
      console.error('Error downloading report:', error);
      showErrorToast("Failed to download report");
    } finally {
      setIsDownloading(false);
    }
  };

  // A simple loading spinner component
  const LoadingSpinner = () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );

  // An error state component
  const ErrorState = () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
      <div className="text-red-500 text-center">
        <div className="text-5xl mb-2">⚠️</div>
        <div>Failed to load</div>
      </div>
    </div>
  );

  return (
    <>
      <Button
        onClick={() => {
          setModalIntent('report');
          setIsModalOpen(true);
        }}
        disabled={disabled || isDownloading}
        type='button'
        className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
      >
        {isDownloading ? <Loader2 className="animate-spin" /> : <Download size={20} />}
        {isDownloading ? 'Generating PDF...' : 'Generate Report'}
      </Button>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-[80vw] max-h-[90vh] overflow-y-auto ">
          <DialogHeader>
            <DialogTitle> Report Configuration</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Report Options Section */}
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold">Report Sections</h3>
                <div className="space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[...disabledOptions, ...reportOptions].map(option => (
                  <div key={option.id} className={`flex items-center space-x-2 ${disabledOptions.find(o => o.id === option.id) ? 'opacity-50' : ''}`} >
                    <Checkbox
                      id={option.id}
                      disabled={disabledOptions.find(o => o.id === option.id)}
                      checked={disabledOptions.find(o => o.id === option.id) ? true : selectedOptions.includes(option.id)}
                      onCheckedChange={() => handleOptionToggle(option.id)}
                    />
                    <label htmlFor={option.id} className="text-sm font-medium">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Images Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Images</h3>
              <Tabs
                value={currentPalletId}
                onValueChange={setCurrentPalletId}
                className="w-full flex"
              >
                <TabsList className="w-[170px] h-[600px] overflow-y-auto bg-light-blue flex flex-col justify-start space-y-4 pt-10">
                  <p className="text-md text-black text-left font-semibold px-5">Types</p>
                  <div>
                    {palletTypes.map((pallet) => (
                      <TabsTrigger
                        key={pallet.id}
                        value={pallet.id?.toString() || ''}
                        className="justify-start p-3 px-5 text-left w-full"
                      >
                        <div className="flex flex-col gap-1">
                          <div>{pallet.variant}</div>
                          <div>{pallet.name}</div>
                        </div>
                      </TabsTrigger>
                    ))}
                  </div>
                </TabsList>

                <div className="flex-1 pl-4">
                  {palletTypes.map((pallet) => (
                    <TabsContent
                      key={pallet.id}
                      value={pallet.id?.toString() || ''}
                      className="h-full"
                    >
                      <div className="border rounded-lg p-4">
                        <h4 className="text-lg font-semibold mb-4">
                          Pallet Name: {pallet.name} - Mode {pallet.shippingModes?.map((sp) => sp.mode).join(', ') || 'N/A'}
                        </h4>
                        <Tabs
                          value={currentImageTab}
                          onValueChange={setCurrentImageTab}
                          className="w-full"
                        >
                          <TabsList className="flex justify-start gap-3 bg-white mb-4">
                            {["gpsRoute", "preship", "postship", "stacking", 'unit-load-images', 'Pre-Ship Rql', 'Post-Ship Rql', 'Post-Stack Rql'].map(
                              (item) => (
                                <TabsTrigger
                                  key={item}
                                  value={item}
                                  className="w-fit data-[state=active]:bg-blue data-[state=active]:text-white border border-blue rounded-lg py-2 text-sm data-[state=inactive]:bg-states-disabled px-5 data-[state=inactive]:border-0"
                                >
                                  {item.charAt(0).toUpperCase() + item.slice(1)}
                                </TabsTrigger>
                              )
                            )}
                          </TabsList>

                          {["gpsRoute", "preship", "postship", "stacking", 'unit-load-images', 'Pre-Ship Rql', 'Post-Ship Rql', 'Post-Stack Rql'].map(
                            (tab) => (
                              <TabsContent
                                key={tab}
                                value={tab}
                              >
                                <div className="grid grid-cols-4 gap-4">
                                  {groupedMedia[Number(pallet.id)]?.[tab as ImageCategory]?.map((item: MediaItem) => (
                                    <div key={item.id} className="relative group">
                                      <div className="absolute top-2 right-2 z-10">
                                        <Checkbox
                                          checked={selectedImages.includes(item.id)}
                                          onCheckedChange={() => handleImageToggle(item.id)}
                                        />
                                      </div>
                                      {item.isLoading ? (
                                        <LoadingSpinner />
                                      ) : item.hasError ? (
                                        <ErrorState />
                                      ) : (
                                        <img
                                          src={item.localUrl || item.url}
                                          alt={`${tab} image`}
                                          className="w-full h-48 object-cover rounded-lg"
                                          onError={() => updateMediaItemLoadingState(item.id, false, true)}
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </TabsContent>
                            )
                          )}
                        </Tabs>
                      </div>
                    </TabsContent>
                  ))}
                </div>
              </Tabs>
            </div>


          </div>
          <DialogFooter className="flex justify-end gap-2 pt-4 sticky bottom-0">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            {modalIntent === 'report' && (
              <Button
                onClick={handleDownload}
                type='button'
                disabled={isDownloading}
                className="bg-states-success"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Generate Report
                  </>
                )}
              </Button>
            )}
           {showPushTo3DX && modalIntent === 'push3dx' && (
             <Button
                type="button"
                onClick={onPushTo3Dx}
                disabled={pushTo3DXDisabled || isPushingPdf}
                className="bg-blue hover:bg-[#00336C]"
              >
                {isPushingPdf ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Push to 3DX
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

DownloadButton.displayName = 'DownloadButton';

export default DownloadButton;

